import { useMemo } from 'react'

import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { format } from 'date-fns'
import { Chart } from 'react-chartjs-2'

import { DATE_DAY_MONTH_SHORT_FORMAT } from 'constants/index'

import {
  BAR_ALPHA,
  BAR_STYLE,
  COUNT_AXIS,
  LINE_STYLE,
  METRIC_CHART_HEIGHT,
  METRIC_CHART_SCALES,
  PERCENT_AXIS,
} from '../chartConfig'
import { MetricDailyPoint } from '../types'

interface Props {
  daily: MetricDailyPoint[]
  color: string
  tooltipLabel?: string
  height?: number
}

export const ExceptionMetricChart = ({
  daily,
  color,
  tooltipLabel = 'Jobs',
  height = METRIC_CHART_HEIGHT,
}: Props) => {
  const { labels, counts, percentages, totals } = useMemo(
    () => ({
      labels: daily.map((point) => format(new Date(point.date), DATE_DAY_MONTH_SHORT_FORMAT)),
      counts: daily.map((point) => point.count),
      percentages: daily.map((point) => Number(point.percentage)),
      totals: daily.map((point) => point.totalJobs),
    }),
    [daily],
  )

  const data = useMemo<ChartData<'bar' | 'line', number[], string>>(
    () => ({
      labels,
      datasets: [
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: 'Number of jobs',
          data: counts,
          backgroundColor: alpha(color, BAR_ALPHA),
          yAxisID: COUNT_AXIS,
          order: 2,
        },
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: '% of total jobs',
          data: percentages,
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          yAxisID: PERCENT_AXIS,
          order: 1,
        },
      ],
    }),
    [labels, counts, percentages, color],
  )

  const options = useMemo<ChartOptions<'bar' | 'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: METRIC_CHART_SCALES,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) =>
              context.dataset.yAxisID === PERCENT_AXIS
                ? `% of total jobs: ${percentages[context.dataIndex]}%`
                : `${tooltipLabel}: ${counts[context.dataIndex]} of ${
                    totals[context.dataIndex]
                  } jobs completed`,
          },
        },
      },
    }),
    [counts, percentages, totals, tooltipLabel],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'bar' | 'line', number[], string> type='bar' data={data} options={options} />
    </div>
  )
}
